import { useState } from "react";
import { BadgeCheck, Play } from "lucide-react";
import { authority } from "@/content/siteContent";
import { Reveal } from "./Reveal";

export default function Authority() {
  const [active, setActive] = useState(0);
  const [ratio, setRatio] = useState(9 / 16);

  return (
    <section id="sobre" className="border-y border-metal bg-graphite py-24 md:py-32" data-testid="authority-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 md:px-8 lg:grid-cols-[1.15fr_1fr]">
        <Reveal className="relative">
          <div className="stripes absolute -right-3 -top-3 z-10 hidden h-24 w-24 rounded-tr-[20px] opacity-90 sm:block" />
          <div className="relative overflow-hidden rounded-[20px] border border-metal bg-ink" style={{ boxShadow: "var(--shadow-industrial)" }}>
            <video
              key={authority.videos[active].src}
              controls
              playsInline
              preload="metadata"
              poster={authority.videos[active].poster}
              onLoadedMetadata={(e) => setRatio(e.target.videoWidth / e.target.videoHeight)}
              className="mx-auto block h-auto max-h-[75vh] w-auto max-w-full object-contain"
              style={{ aspectRatio: `${ratio}` }}
              data-testid="authority-video-player"
            >
              <source src={authority.videos[active].src} type="video/mp4" />
              <source src={authority.videos[active].src.replace(".mp4", ".webm")} type="video/webm" />
            </video>
            <div className="pointer-events-none absolute left-0 top-0 bg-gradient-to-b from-ink/85 via-ink/30 to-transparent p-5 md:p-7">
              <p className="microtitle">{authority.role}</p>
              <p className="mt-1 font-display text-xl font-extrabold text-paper md:text-2xl" data-testid="authority-name">{authority.name}</p>
            </div>
            <span className="absolute right-4 top-4 rounded-full border border-metal bg-ink/85 px-3 py-1 font-display text-[0.58rem] font-extrabold tracking-[0.18em] text-brand backdrop-blur-sm" data-testid="authority-video-label">
              {authority.videos[active].label}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-4 gap-2.5 sm:grid-cols-7" data-testid="authority-thumbnails">
            {authority.videos.map((video, i) => (
              <button
                key={video.src}
                onClick={() => setActive(i)}
                aria-label={`Assistir: ${video.label}`}
                className={`group relative aspect-[9/11] overflow-hidden rounded-2xl border transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 ${i === active ? "border-brand shadow-[0_0_20px_rgba(245,176,0,0.2)]" : "border-metal hover:border-brand/50"}`}
                data-testid={`authority-thumb-${i + 1}`}
              >
                <img src={video.poster} alt={video.label} loading="lazy" className="h-full w-full object-cover" />
                <div className={`absolute inset-0 transition-colors duration-300 ${i === active ? "bg-ink/10" : "bg-ink/45 group-hover:bg-ink/20"}`} />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-full border backdrop-blur-sm transition-colors duration-300 ${i === active ? "border-brand bg-brand text-ink" : "border-metal bg-ink/70 text-paper group-hover:border-brand group-hover:text-brand"}`}>
                    <Play className="h-4 w-4 fill-current" />
                  </span>
                </span>
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/90 to-transparent px-2 pb-2 pt-5 text-center font-display text-[0.5rem] font-extrabold tracking-[0.12em] text-paper">
                  {video.label}
                </span>
              </button>
            ))}
          </div>
        </Reveal>

        <div>
          <Reveal>
            <div className="microtitle-row mb-5">
              <p className="microtitle">{authority.microtitle}</p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-paper sm:text-5xl" data-testid="authority-title">
              {authority.titleA}
              <br />
              {authority.titleB}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-mist">{authority.description}</p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="authority-specialties">
              {authority.specialties.map((s) => (
                <span key={s} className="flex items-center gap-2 rounded-full border border-metal bg-ink px-4 py-2 text-xs font-bold tracking-wide text-mist">
                  <BadgeCheck className="h-3.5 w-3.5 text-brand" />
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.28}>
            <p className="mt-8 flex items-start gap-3 rounded-2xl border border-brand/40 bg-ink p-5 text-sm font-semibold leading-relaxed text-paper" data-testid="authority-crt-badge">
              <BadgeCheck className="mt-0.5 h-6 w-6 flex-shrink-0 text-brand" />
              <span>
                <span className="font-display text-[0.72rem] font-extrabold uppercase tracking-[0.16em] text-brand">Credenciamento</span>
                <br />
                {authority.certNote}
              </span>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
